import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        name1
        name2
        nameWithLp
        formName
      }
    }
  }
`;

const NAMES_FIELDS: Record<string, { apiField: string }> = {
  formName: { apiField: 'formName' },
  name1: { apiField: 'name1' },
  name2: { apiField: 'name2' },
  nameWithLp: { apiField: 'nameWithLp' }
};

export interface EditNamesDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditNamesDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditNamesDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save names');
      } else {
        toast.success('Names saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving names:', error);
      toast.error('Failed to save names');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Names"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Name1"
          onChange={v => {
            updateField('name1', v);
          }}
          value={value.name1}
        />
        <EditFormField
          label="Name2"
          onChange={v => {
            updateField('name2', v);
          }}
          value={value.name2}
        />
        <EditFormField
          label="Name With LP"
          onChange={v => {
            updateField('nameWithLp', v);
          }}
          value={value.nameWithLp}
        />
        <EditFormField
          label="Form Name"
          onChange={v => {
            updateField('formName', v);
          }}
          value={value.formName}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, null | string> {
  const changes: Record<string, null | string> = {};

  for (const [uiField, config] of Object.entries(NAMES_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      const strVal = currentVal as string;
      changes[config.apiField] = strVal === '' ? null : strVal;
    }
  }

  return changes;
}
